import { yupResolver } from '@hookform/resolvers/yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import AddIcon from '@material-ui/icons/Add';
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Box from '@mui/material/Box';
import { isValid } from 'clients/Clients';
import { createComment } from 'clients/WebServiceClient';
import clsx from 'clsx';
import NotificationModal from 'components-v2/mocules/NotificationModal';
import { useUploadSingleFile } from 'hooks/useUploadSingleFile';
import NextImage from 'next/image';
import { ChangeEvent, useRef, useState } from 'react';
import { Controller, get, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getLinkCDNImage } from 'utils/ImageUtils';
import NotifyUtils from 'utils/NotifyUtils';
import { QAValues, schema } from '../constants';
import styles from './styles.module.css';

interface Props {
  isOpen: boolean;
  targetCode: string;
  onClose: () => void;
}

const MAX_IMAGES = 3;
const MAX_SIZE_MB = 4;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const RegisteredQuestionForm = (props: Props) => {
  const { isOpen, targetCode, onClose } = props;
  const { control, handleSubmit, formState, reset } = useForm({
    resolver: yupResolver(schema),
  });
  const [images, setImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { onChange: uploadOnChange, isLoading: isUploading } = useUploadSingleFile({
    maxFileSize: MAX_SIZE_MB,
    callback: async (url: string) => {
      if (url) {
        setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, url] : prev));
        return true;
      }
      return false;
    },
  });

  const handleClose = () => {
    reset();
    setImages([]);
    onClose();
  };

  const handleOpenFilePicker = () => {
    if (images.length >= MAX_IMAGES || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ACCEPTED_TYPES.includes(file.type) || !ACCEPTED_EXTENSIONS.includes(extension)) {
        NotifyUtils.error('Chỉ hỗ trợ JPG, JPEG, PNG');
        e.target.value = '';
        return;
      }
    }
    uploadOnChange(e);
    e.target.value = '';
  };

  const handleRemoveImage = (url: string) => {
    setImages((prev) => prev.filter((img) => img !== url));
  };

  const onSubmit = async (data: QAValues) => {
    const { question } = data;

    const params = {
      content: question,
      targetCode,
      images,
    };

    const rs = await createComment(params);

    if (!isValid(rs)) {
      toast.error(rs.message || 'Có lỗi xảy ra, vui lòng thử lại');
      return;
    }

    reset();
    setImages([]);
    onClose();
    toast.success('Cảm ơn bạn đã gửi ý kiến cho thuocsi.vn. Ý kiến của bạn đang chờ duyệt.', { autoClose: 3000 });
  };

  return (
    <NotificationModal
      classNames={styles.loggedFormModal}
      title="Ý kiến về sản phẩm"
      maxWidth="sm"
      cancelText="Hủy"
      submitText="Gửi"
      isOpen={isOpen}
      isDisableSubmit={formState.isSubmitting || isUploading}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form className={styles.loggedForm}>
        <Box className={styles.loggedFormGroup}>
          <Controller
            name="question"
            control={control}
            render={({ field }) => {
              const error = get(formState.errors, field.name);
              return (
                <Box className={styles.formTextarea}>
                  <TextareaAutosize
                    className={clsx(styles.input, { [styles.error]: !!error })}
                    minRows={3}
                    maxRows={3}
                    maxLength={200}
                    placeholder="* Vui lòng nhập nội dung câu hỏi bằng tiếng Việt có dấu, tối thiểu 3 ký tự (Bắt buộc)"
                    {...field}
                  />
                  {error ? <span>{error.message}</span> : <div className={styles.textareaCount}>{field.value ? field.value.length : 0}/ 200</div>}
                </Box>
              );
            }}
          />
          <Box
            className={clsx(styles.uploadBox, {
              [styles.uploadBoxDisabled]: images.length >= MAX_IMAGES || isUploading,
            })}
            onClick={handleOpenFilePicker}
          >
            <Box className={styles.uploadIcon}>
              <CameraAltOutlinedIcon />
            </Box>
            <Box className={styles.uploadText}>
              <span className={styles.uploadTitle}>{isUploading ? 'Đang tải ảnh lên...' : 'Thêm ảnh (tùy chọn)'}</span>
              <span className={styles.uploadSubtitle}>Tối đa {MAX_IMAGES} ảnh • JPG, PNG, JPEG • Mỗi ảnh ≤ {MAX_SIZE_MB}MB</span>
            </Box>
            <Box className={styles.uploadAddBtn}>
              {isUploading ? <CircularProgress color="inherit" size={18} thickness={4} style={{ color: 'var(--primary-color)' }} /> : <AddIcon />}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              hidden
              onChange={handleSelectFile}
            />
          </Box>
          {images.length > 0 && (
            <Box className={styles.thumbnails}>
              {images.map((url) => (
                <Box key={url} className={styles.thumbnailItem}>
                  <NextImage src={getLinkCDNImage(url)} width={80} height={80} className={styles.thumbnailImage} />
                  <Box
                    className={styles.thumbnailRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(url);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </form>
    </NotificationModal>
  );
};

export default RegisteredQuestionForm;
